import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Notification } from "../components/ui/notification";
import { ConversionEventsSection } from "../components/settings/ConversionEventsSection";
import {
  disconnect,
  fetchBootstrap,
  listMyShareLinks,
  logout,
  OfflineGrantRequiredError,
  revokeMyShareLink,
  sendTestDigest,
  subscribeDigest,
  unsubscribeDigest,
  type BootstrapResponse,
  type MineShareLink,
} from "../lib/api";
import { withBase } from "../lib/base-path";
import { cn } from "../lib/utils";
import { pushGtmEvent } from "../lib/gtm-events";
import { reportHandledError } from "../lib/bugsnag";
import { usePageMeta } from "../hooks/usePageMeta";

// Encodes a "re-enable digest on return" intent across the Google OAuth
// redirect. The Settings page writes this as a query param on /app/settings,
// then reads it on load and upserts the subscription.
const DIGEST_INTENT_PARAM = "digest_intent";

function modeLabel(mode: BootstrapResponse["grantMode"]): string {
  switch (mode) {
    case "offline":
      return "Offline";
    case "ephemeral":
      return "Live-only";
    default:
      return "Not connected";
  }
}

function modePillClass(mode: BootstrapResponse["grantMode"]): string {
  switch (mode) {
    case "offline":
      return "bg-green-50 text-green-700 ring-green-600/20";
    case "ephemeral":
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    default:
      return "bg-neutral-50 text-neutral-600 ring-neutral-950/10";
  }
}

export function SettingsPage() {
  usePageMeta({ title: "Settings · Traffic Snapshot" });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [busy, setBusy] = useState<"logout" | "disconnect" | "digest" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [shareLinks, setShareLinks] = useState<MineShareLink[] | null>(null);
  const [revokingShareId, setRevokingShareId] = useState<string | null>(null);
  // M12.7 — local state for the "Send sample report now" button. Three
  // states: idle (null), sending ("sending"), sent within the last ~5s
  // ("sent"). Errors bubble through the top-level error notification.
  const [testDigestState, setTestDigestState] = useState<"idle" | "sending" | "sent">("idle");

  const refreshBootstrap = useCallback(async () => {
    const fresh = await fetchBootstrap();
    setBootstrap(fresh);
    return fresh;
  }, []);

  useEffect(() => {
    refreshBootstrap().catch((e: Error) => setError(e.message));
  }, [refreshBootstrap]);

  const refreshShareLinks = useCallback(async () => {
    try {
      const rows = await listMyShareLinks();
      setShareLinks(rows);
    } catch (e) {
      // Silent — the section just won't render. Not worth raising to the
      // top-level error surface. Report to Bugsnag so persistent failures
      // surface without polluting the UI.
      console.error("listMyShareLinks failed:", e);
      reportHandledError(e, { source: "SettingsPage.listMyShareLinks" });
      setShareLinks([]);
    }
  }, []);

  useEffect(() => {
    if (!bootstrap?.authenticated) return;
    refreshShareLinks();
  }, [bootstrap, refreshShareLinks]);

  async function handleRevokeShare(id: string) {
    setRevokingShareId(id);
    try {
      await revokeMyShareLink(id);
      setToast("Share link revoked.");
      await refreshShareLinks();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to revoke share link");
    } finally {
      setRevokingShareId(null);
    }
  }

  // Auto-apply a pending "enable digest" intent once Google hands us back an
  // offline grant (see digest toggle handler below). We only call subscribe
  // once; the URL is cleaned regardless so a refresh doesn't re-trigger.
  useEffect(() => {
    if (!bootstrap?.authenticated) return;
    const intent = params.get(DIGEST_INTENT_PARAM);
    if (!intent) return;
    if (bootstrap.grantMode !== "offline") {
      const next = new URLSearchParams(params);
      next.delete(DIGEST_INTENT_PARAM);
      setParams(next, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await subscribeDigest({ propertyId: intent, enabled: true });
        if (cancelled) return;
        setToast("Weekly digest enabled. We'll email it Mondays.");
        await refreshBootstrap();
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to enable digest");
      } finally {
        const next = new URLSearchParams(params);
        next.delete(DIGEST_INTENT_PARAM);
        setParams(next, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrap, params, refreshBootstrap, setParams]);

  async function handleLogout() {
    setBusy("logout");
    try {
      await logout();
      navigate("/");
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  }

  async function handleDisconnect() {
    if (!window.confirm("Disconnect your Google account and delete your Snapshot data?")) return;
    setBusy("disconnect");
    try {
      await disconnect();
      navigate("/");
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  }

  const defaultPropertyId = bootstrap?.defaultProperty?.propertyId ?? null;
  const defaultPropertyName = bootstrap?.defaultProperty?.propertyName ?? null;
  const digestEnabledForDefault = useMemo(() => {
    if (!defaultPropertyId) return false;
    return bootstrap?.digestSubscriptions?.some((s) => s.propertyId === defaultPropertyId) ?? false;
  }, [bootstrap, defaultPropertyId]);

  async function handleDigestToggle(nextEnabled: boolean) {
    if (!defaultPropertyId) return;
    setBusy("digest");
    setError(null);
    try {
      if (nextEnabled) {
        await subscribeDigest({ propertyId: defaultPropertyId, enabled: true });
        // M13.7 — weekday is hardcoded to Monday on the server today,
        // so 1 reflects the current digest cron. Fold the real value in
        // here once the user picks a weekday in-app.
        pushGtmEvent("digest_enabled", { property_id: defaultPropertyId, weekday: 1 });
        setToast("Weekly digest enabled. We'll email it Mondays.");
      } else {
        await unsubscribeDigest(defaultPropertyId);
        pushGtmEvent("digest_disabled", { property_id: defaultPropertyId });
        setToast("Weekly digest turned off.");
      }
      await refreshBootstrap();
    } catch (e) {
      if (e instanceof OfflineGrantRequiredError) {
        // Redirect through Google's consent screen. On return the effect
        // above will auto-enable the digest using the intent param.
        const returnTo = `/app/settings?${DIGEST_INTENT_PARAM}=${encodeURIComponent(defaultPropertyId)}`;
        window.location.href = withBase(
          `/auth/google/start?offline=true&return_to=${encodeURIComponent(returnTo)}`,
        );
        return;
      }
      setError(e instanceof Error ? e.message : "Digest update failed");
    } finally {
      setBusy(null);
    }
  }

  function handleUpgrade() {
    window.location.href = withBase(
      `/auth/google/start?offline=true&return_to=${encodeURIComponent("/app/settings")}`,
    );
  }

  const grantMode = bootstrap?.grantMode ?? "none";

  return (
    <AppShell
      eyebrow="Settings"
      title={
        <>
          Account, <em className="font-serif text-purple-700">data</em> &amp; disconnect
        </>
      }
      subtitle="What we store about you and how to remove it."
    >
      {error ? (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {toast ? (
        <Notification
          variant="success"
          title={toast}
          className="mb-6"
          onDismiss={() => setToast(null)}
        />
      ) : null}

      <div className="space-y-10">
        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            Signed in as
          </p>
          <div className="mt-3 flex items-center gap-4 rounded-xl bg-white p-5 ring-1 ring-neutral-950/5">
            {bootstrap?.user?.picture ? (
              <img
                src={bootstrap.user.picture}
                alt=""
                className="size-10 rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span aria-hidden="true" className="size-10 rounded-full bg-purple-100" />
            )}
            <div>
              <p className="font-display text-sm font-semibold text-neutral-950">
                {bootstrap?.user?.name ?? "—"}
              </p>
              <p className="text-sm text-neutral-500">{bootstrap?.user?.email ?? "—"}</p>
            </div>
          </div>
        </section>

        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            Access mode
          </p>
          <div className="mt-3 rounded-xl bg-white p-5 ring-1 ring-neutral-950/5">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ring-1 ring-inset ${modePillClass(
                  grantMode,
                )}`}
              >
                {modeLabel(grantMode)}
              </span>
              <p className="text-sm text-neutral-500">
                {grantMode === "offline"
                  ? "We can refresh your GA4 connection on your behalf — required for the weekly digest and any scheduled exports."
                  : grantMode === "ephemeral"
                    ? "We only hold a short-lived access token on this device. Upgrade to enable the weekly digest."
                    : "Connect your GA4 account to get started."}
              </p>
            </div>
            <AccessTokenExpiryLine
              expiresAt={bootstrap?.accessTokenExpiresAt ?? null}
              offline={grantMode === "offline"}
            />
            {grantMode === "ephemeral" ? (
              <div className="mt-4">
                {/* Google branding spec — same official sign-in button
                 *  used on /app/connect, just with the offline-upgrade
                 *  label so the consent screen the user lands on
                 *  matches the affordance they clicked. */}
                <GoogleSignInButton
                  onClick={handleUpgrade}
                  label="Continue with Google for extended access"
                />
                <p className="mt-2 text-xs text-neutral-500 text-pretty">
                  Sends you through Google to re-consent with offline access. We store your refresh
                  credentials securely encrypted; hitting{" "}
                  <strong className="font-semibold text-neutral-700">Disconnect</strong> below
                  revokes them immediately and deletes our copy.
                </p>
              </div>
            ) : null}
          </div>
        </section>

        <section id="weekly-digest" className="scroll-mt-24">
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            Weekly digest
          </p>
          <div className="mt-3 rounded-xl bg-white p-5 ring-1 ring-neutral-950/5">
            {defaultPropertyId ? (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-sm font-semibold text-neutral-950">
                      Email me a traffic digest every Monday
                    </p>
                    <p className="mt-1 text-sm text-neutral-500 text-pretty">
                      Sent to{" "}
                      <span className="font-medium text-neutral-700">
                        {bootstrap?.user?.email ?? "your signed-in email"}
                      </span>{" "}
                      for{" "}
                      <span className="font-medium text-neutral-700">{defaultPropertyName}</span>.
                      Contains the last 7 days vs. the prior 7 — KPIs, top platforms, top landing
                      pages, and any UTM issues we spotted.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={digestEnabledForDefault}
                    aria-label="Weekly digest"
                    disabled={busy === "digest"}
                    onClick={() => handleDigestToggle(!digestEnabledForDefault)}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2 disabled:opacity-50 ${
                      digestEnabledForDefault ? "bg-purple-500" : "bg-neutral-200"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-block size-5 rounded-full bg-white shadow transition-transform ${
                        digestEnabledForDefault ? "translate-x-[22px]" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
                <p className="mt-3 text-xs text-neutral-500 text-pretty">
                  You can unsubscribe at any time — one click in the email footer, or flip the
                  toggle back off here.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-neutral-100 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={testDigestState === "sending" || !defaultPropertyId}
                    onClick={async () => {
                      if (!defaultPropertyId) return;
                      setTestDigestState("sending");
                      setError(null);
                      try {
                        await sendTestDigest(defaultPropertyId);
                        setTestDigestState("sent");
                        setToast("Sample digest sent — check your inbox.");
                        window.setTimeout(() => setTestDigestState("idle"), 5000);
                      } catch (e) {
                        setTestDigestState("idle");
                        setError(
                          e instanceof Error ? e.message : "Couldn't send the sample digest.",
                        );
                      }
                    }}
                    className="text-purple-700"
                  >
                    <i
                      className={cn(
                        "fa-regular text-sm",
                        testDigestState === "sending"
                          ? "fa-spinner fa-spin"
                          : testDigestState === "sent"
                            ? "fa-circle-check text-green-600"
                            : "fa-paper-plane",
                      )}
                      aria-hidden="true"
                    />
                    {testDigestState === "sending"
                      ? "Sending…"
                      : testDigestState === "sent"
                        ? "Sent"
                        : "Send sample report now"}
                  </Button>
                  <p className="text-xs text-neutral-500">
                    Get a one-off copy of the digest to preview what Monday's email looks like.
                  </p>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-500">
                Pick a default GA4 property first — the digest sends for that property.
              </p>
            )}
          </div>
        </section>

        <section id="conversion-events" className="scroll-mt-24">
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            Conversion events
          </p>
          <span
            aria-hidden="true"
            className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500"
          />
          <div className="mt-3">
            <ConversionEventsSection
              propertyId={defaultPropertyId}
              propertyName={defaultPropertyName}
            />
          </div>
        </section>

        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            Shared reports
          </p>
          <div className="mt-3 space-y-3">
            {shareLinks === null ? (
              <div className="h-12 animate-pulse rounded-xl bg-neutral-100" aria-busy="true" />
            ) : shareLinks.length === 0 ? (
              <p className="rounded-xl bg-white p-5 text-sm text-neutral-600 ring-1 ring-neutral-950/5">
                You haven't shared any reports yet. Use the{" "}
                <span className="font-medium text-neutral-800">Share report</span> button on the
                Traffic report page to mint an anonymous read-only link with a chosen expiry.
              </p>
            ) : (
              <ul role="list" className="space-y-2">
                {shareLinks.map((link) => (
                  <ShareLinkRow
                    key={link.id}
                    link={link}
                    revoking={revokingShareId === link.id}
                    onRevoke={() => handleRevokeShare(link.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>

        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            What we store
          </p>
          <ul role="list" className="mt-3 space-y-2 text-sm text-neutral-700">
            <li>Your Google email, name, and profile picture.</li>
            <li>A signed session cookie — 7 days.</li>
            <li>
              Your most-recently-picked GA4 property ID + name so the report loads faster next time.
            </li>
            <li>
              <strong>Only if you opt into the weekly digest:</strong> an encrypted copy of your
              Google refresh credentials so we can fetch your report on your behalf each week.
            </li>
          </ul>
          <p className="mt-3 text-sm text-neutral-500 text-pretty">
            No GA4 row data is stored durably. Reports are fetched live and cached for up to an hour
            in our data store.
          </p>
        </section>

        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
            Sign out vs disconnect
          </p>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl bg-white p-5 ring-1 ring-neutral-950/5">
              <p className="font-display text-sm font-semibold text-neutral-950">Sign out</p>
              <p className="mt-1 text-sm text-neutral-500 text-pretty">
                Clears the session cookie on this device. Your Google authorization and Snapshot
                preferences stay put.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={handleLogout}
                disabled={busy !== null}
              >
                {busy === "logout" ? "Signing out…" : "Sign out"}
              </Button>
            </div>
            <div className="rounded-xl bg-white p-5 ring-1 ring-neutral-950/5">
              <p className="font-display text-sm font-semibold text-neutral-950">
                Disconnect &amp; delete
              </p>
              <p className="mt-1 text-sm text-neutral-500 text-pretty">
                Revokes our access to your Google account, deletes your Traffic Snapshot record
                (including any stored refresh credentials), and clears your property preferences.
                Past exports expire on their normal schedule.
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="mt-4"
                onClick={handleDisconnect}
                disabled={busy !== null}
              >
                {busy === "disconnect" ? "Disconnecting…" : "Disconnect & delete my data"}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function AccessTokenExpiryLine({
  expiresAt,
  offline,
}: {
  expiresAt: number | null;
  offline: boolean;
}) {
  if (!expiresAt) return null;
  const now = Date.now();
  if (expiresAt <= now) {
    return (
      <p className="mt-3 text-xs text-neutral-500">
        <span className="font-medium text-amber-700">Access expired.</span>{" "}
        {offline
          ? "We'll reconnect with your stored credentials the next time you load a report."
          : "Reconnect from the Connect page to load a report."}
      </p>
    );
  }
  // Short-form relative time — matches the token's ~1h TTL window; falls
  // back to an absolute local timestamp for longer-lived grants.
  const deltaMs = expiresAt - now;
  const mins = Math.round(deltaMs / 60_000);
  const hours = Math.round(deltaMs / (60 * 60_000));
  const absolute = new Date(expiresAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const friendly =
    mins < 60 ? `${mins} minute${mins === 1 ? "" : "s"}` : `${hours} hour${hours === 1 ? "" : "s"}`;
  return (
    <p className="mt-3 text-xs text-neutral-500">
      Current live connection valid for about{" "}
      <span className="font-medium text-neutral-700">{friendly}</span>{" "}
      <span className="text-neutral-400">(expires {absolute})</span>.
    </p>
  );
}

function ShareLinkRow({
  link,
  revoking,
  onRevoke,
}: {
  link: MineShareLink;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const expired = link.expiresAt < Date.now();
  const expiresLabel = expired
    ? "Expired"
    : `Expires ${new Date(link.expiresAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent — the URL is visible in the input for manual copy.
    }
  }

  return (
    <li className="flex flex-col gap-3 rounded-xl bg-white p-4 ring-1 ring-neutral-950/5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset",
              expired
                ? "bg-amber-50 text-amber-800 ring-amber-600/20"
                : "bg-green-50 text-green-700 ring-green-600/20",
            )}
          >
            {expiresLabel}
          </span>
          <span className="font-mono text-xs text-neutral-500">Property {link.propertyId}</span>
        </div>
        <p className="mt-2 truncate font-mono text-xs text-neutral-700" title={link.url}>
          {link.url}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
          <i
            className={cn(
              "fa-regular",
              copied ? "fa-check text-emerald-600" : "fa-copy",
              "text-xs",
            )}
            aria-hidden="true"
          />
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onRevoke} disabled={revoking}>
          {revoking ? "Deleting…" : "Delete"}
        </Button>
      </div>
    </li>
  );
}
